/**
 * Buyer Hub access gate — resolves whether the current user is a Hub-sent A1.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useCorporateAccess } from "@/hooks/useCorporateAccess";
import { hubSendService } from "@/services/buyerHub/hubSend";
import type { BuyerHubAccessState } from "@/types/BuyerHub";

const LOADING_STATE: BuyerHubAccessState = {
  isHubBuyerUser: false,
  hubCorporateId: null,
  hubSent: null,
  loading: true,
};

const NO_ACCESS_STATE: BuyerHubAccessState = {
  isHubBuyerUser: false,
  hubCorporateId: null,
  hubSent: null,
  loading: false,
};

/** Resolves Hub-sent buyer access for route isolation (U1). */
export function useBuyerHubAccess(): BuyerHubAccessState {
  const { loading: authLoading, hasRole } = useAuth();
  const { corporateIds, loading: accessLoading } = useCorporateAccess();

  const isAdmin = hasRole("admin");
  const isManager = hasRole("manager");
  const isOperator = isAdmin || isManager;

  const { data: sentRecords = [], isLoading: sentLoading } = useQuery({
    queryKey: ["buyer-hub-sent", corporateIds.join(",")],
    queryFn: () => hubSendService.listActiveSentForCorporates(corporateIds),
    enabled: !isOperator && !authLoading && !accessLoading && corporateIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  if (authLoading || accessLoading || sentLoading) {
    return LOADING_STATE;
  }

  if (isOperator || sentRecords.length === 0) {
    return NO_ACCESS_STATE;
  }

  const hubSent = sentRecords[0] ?? null;

  return {
    isHubBuyerUser: hubSent !== null,
    hubCorporateId: hubSent?.corporateId ?? null,
    hubSent,
    loading: false,
  };
}
